<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <div
      class="device-icon-pin"
      :class="[sizeClass, isLocked ? 'device-active--lock' : '']"
      :style="{ '--pin-active-color': 'var(--device-color-lock)' }"
      :title="device.deviceName">
      <ControlStatusRing :value="isLocked ? 100 : 0">
        <div class="lock-pin-art">
          <LockAnimation :kind="lockKind" :locked="isLocked" />
        </div>
      </ControlStatusRing>
      <div v-if="isLocked" class="device-icon-pin__glow"></div>
    </div>
  </template>

  <!-- ── Card mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'card'">
    <!-- Card face: the lock's picture, its name and its state; the largest size
         carries the two verbs. -->
    <DeviceCardShell
      :size="cardSize ?? 'min'"
      :name="device.deviceName"
      :title="device.deviceName"
      :active="isUnlocked"
      :accent="cardAccent"
      class="lock-card-face"
      :class="{ 'lock-card-face--unlocked': isUnlocked }"
      @click="handleToggle"
      @contextmenu.prevent="emit('open-control-modal')">
      <template #media>
        <!-- Raw <img> rather than DeviceThumbnail: that component is a fixed 2rem
             circle for the compact tile, which left the card's photo tiny. -->
        <img v-if="thumbUrl" :src="thumbUrl" alt="" class="lock-card-face__img" draggable="false" />
        <LockAnimation v-else class="lock-card-face__figure" :kind="lockKind" :locked="isLocked" />
      </template>

      <template v-if="cardSize !== 'min'" #value>
        <span class="lock-card-face__value">{{
          isLocked ? tt('Locked', 'Zaklenjeno') : tt('Unlocked', 'Odklenjeno')
        }}</span>
      </template>

      <template v-if="cardSize === 'max'" #control>
        <div class="lock-card-face__verbs">
          <button
            type="button"
            class="lock-card-face__verb"
            :class="{ 'lock-card-face__verb--active': isLocked }"
            :disabled="controlling || isLocked"
            @click.stop="handleSetLocked(true)">
            <span class="material-symbols-outlined">lock</span>
            {{ tt('Lock', 'Zakleni') }}
          </button>
          <button
            type="button"
            class="lock-card-face__verb"
            :class="{ 'lock-card-face__verb--active': !isLocked }"
            :disabled="controlling || !isLocked"
            @click.stop="handleSetLocked(false)">
            <span class="material-symbols-outlined">lock_open</span>
            {{ tt('Unlock', 'Odkleni') }}
          </button>
        </div>
      </template>
    </DeviceCardShell>
  </template>

  <!-- ── Grid mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <DeviceGridCell
      :icon="device.icon || (isLocked ? 'lock' : 'lock_open')"
      :name="device.deviceName"
      :is-on="isLocked"
      active-color="red-400"
      @toggle="emit('open-control-modal')"
      @long-press="emit('open-control-modal')" />
  </template>

  <!-- ── Control mode ──────────────────────────────────────────────────── -->
  <!-- The chrome owns the head (card t_488df70b); this body is the hero — the
       lock's own circle under the state word — and the Lock / Unlock chips,
       both off the same write the old body used: `unlock`, or the device's own
       `locked` key when it carries one (utils/lockState → lockWriteKey). -->
  <template v-else-if="mode === 'control'">
    <div class="lock-sheet">
      <div class="lock-hero">
        <button
          type="button"
          class="lock-circle"
          :class="{ 'lock-circle--locked': isLocked, 'lock-circle--busy': controlling }"
          :disabled="controlling"
          @click="handleToggle">
          <div class="lock-circle__art">
            <LockAnimation :kind="lockKind" :locked="isLocked" />
          </div>
        </button>
        <div class="lock-state" :class="{ 'lock-state--locked': isLocked }">
          {{ isLocked ? tt('Locked', 'Zaklenjeno') : tt('Unlocked', 'Odklenjeno') }}
        </div>
        <div class="lock-caption">
          {{ isLocked ? tt('Secure', 'Varno') : tt('Open access', 'Prost dostop') }}
        </div>
      </div>

      <div class="lock-actions">
        <ControlChip :disabled="controlling" :active="isLocked" @click="handleSetLocked(true)">
          {{ tt('Lock', 'Zakleni') }}
        </ControlChip>
        <ControlChip :disabled="controlling" :active="!isLocked" @click="handleSetLocked(false)">
          {{ tt('Unlock', 'Odkleni') }}
        </ControlChip>
      </div>

      <p v-if="lastUpdated" class="lock-updated">{{ tt('Last updated:', 'Zadnja posodobitev:') }} {{ lastUpdated }}</p>

      <!-- Activity timeline -->
      <SetpointTimeline
        :device-values="device.deviceValues ?? []"
        :device-id="device.id"
        accent-color="rgb(249, 115, 22)" />
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { formatTime } from '../../utils/formatDate';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import ControlChip from '../device-sheet/ControlChip.vue';
import DeviceCardShell from './DeviceCardShell.vue';
import { useDeviceValues } from '../../composables/useDeviceValues';
import { useDeviceControl } from '../../composables/useDeviceControl';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import SetpointTimeline from './SetpointTimeline.vue';

import { tt } from '../../utils/consoleText';
import { isLockLocked, lockWriteKey } from '../../utils/lockState';
import { lockKindOf, type LockKind } from './deviceWidgetTypes';
import LockAnimation from './lock/LockAnimation.vue';
import { getDefaultThumbnailUrl } from '../../composables/useDeviceThumbnail';
import type { Device } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'card' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
  /** Set by the pages that grid cards (`DeviceButton`) — `undefined` keeps the
   *  compact button row other surfaces have always drawn. */
  cardSize?: 'min' | 'mid' | 'max';
}

const props = withDefaults(defineProps<Props>(), { mode: 'card', size: 'md', cardSize: undefined });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

// ── State ────────────────────────────────────────────────────────────────
const { currentValues, initializeValues } = useDeviceValues(props.device.id);
const { controlling, setProperty } = useDeviceControl();

if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);
watch(
  () => props.device.deviceValues,
  (v) => {
    if (v?.length) initializeValues(v);
  },
  { deep: true },
);

// The lock's own fold (utils/lockState): a device carrying `locked` — the dev
// hub's Covered Entrance Door Lock — reads IT, the same reading the chrome's
// pill makes, then the legacy `unlock` property and power-key shapes. The
// body, the chips and the tile all follow this one computed.
const isLocked = computed(() => isLockLocked(props.device.deviceValues, currentValues.value));

/**
 * The card's whole visual state hangs on this, inverted: a lock is at rest when
 * it is locked, so only the OPEN lock calls attention (owner, 25.10.2026:
 * *"similar bg for locks.. if locked no effect.. if unlocked then effect"*).
 */
const isUnlocked = computed(() => !isLocked.value);

/** Which lock this is — the editor's pick, then the icon and the name. */
const lockKind = computed<LockKind>(() =>
  lockKindOf({
    icon: props.device.icon,
    deviceName: props.device.deviceName,
    shortName: props.device.shortName,
    deviceType: props.device.deviceType,
    controlType: props.device.deviceControl || props.device.controlType,
    settings: props.device.settings,
  }),
);

/** The unlocked card's tone — the warning amber the sensor cards breathe in. */
const cardAccent = computed<string | null>(() => (isUnlocked.value ? 'var(--lock-card-tone)' : null));

const lastUpdated = computed(() => (Object.keys(currentValues.value).length > 0 ? formatTime(new Date()) : ''));

async function handleToggle() {
  // Write the key the device actually has: a `locked` row takes the desired
  // state in its own spelling (`locked = !locked`); the older shapes keep the
  // `unlock` write useDeviceControl resolves onto their own row.
  await setProperty(props.device, lockWriteKey(props.device.deviceValues), !isLocked.value);
}

/** A chip for a NAMED state: same write as the toggle, no-op when already there. */
async function handleSetLocked(nextLocked: boolean) {
  if (nextLocked === isLocked.value) return;
  await handleToggle();
}

/** The card's picture, resolved through the S3-key blob cache (the one door
 *  the other card faces read `thumbnailUrl` through). */
const thumbUrl = computed(() => getDefaultThumbnailUrl(props.device));
const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));
</script>

<style scoped>
.active .device-icon span,
.active .device-name,
.active .device-current-value {
  color: var(--device-color-lock) !important;
}
.active {
  border-color: var(--device-color-lock) !important;
  background-color: transparent !important;
}

/* ── Control mode: the sheet body ────────────────────────────────────── */
.lock-sheet {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: calc(1rem * var(--ui-space-factor));
}

.lock-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding-top: calc(0.25rem * var(--ui-space-factor));
}

/* The one big control: a 6rem circle, tinted by the lock's own accent while
   locked; the busy state calms it while a write is in flight. */
.lock-circle {
  width: 6rem;
  height: 6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--glass-edge);
  border-radius: 50%;
  background: rgba(148, 163, 184, 0.1);
  color: rgb(var(--gray-500));
  cursor: pointer;
  transition:
    color 0.2s,
    border-color 0.2s,
    background 0.2s,
    box-shadow 0.2s,
    transform 0.15s;
}

.dark .lock-circle {
  background: rgba(255, 255, 255, 0.04);
  color: rgb(var(--gray-400));
}

.lock-circle:hover:not(:disabled) {
  transform: scale(1.03);
}

.lock-circle:disabled {
  cursor: default;
  opacity: 0.65;
}

.lock-circle--locked {
  color: var(--device-color-lock);
  border-color: color-mix(in srgb, var(--device-color-lock) 55%, transparent);
  background: color-mix(in srgb, var(--device-color-lock) 16%, transparent);
  box-shadow: 0 0 18px color-mix(in srgb, var(--device-color-lock) 18%, transparent);
}

.lock-circle--busy {
  animation: lock-busy 1s ease-in-out infinite;
}

@keyframes lock-busy {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

.lock-state {
  font-size: 1.0625rem;
  font-weight: 700;
  line-height: 1.2;
  color: rgb(var(--gray-600));
}

.dark .lock-state {
  color: rgb(var(--gray-200));
}

.lock-state--locked {
  color: var(--device-color-lock);
}

.lock-caption {
  font-size: 0.75rem;
  color: rgb(var(--gray-500));
}

.dark .lock-caption {
  color: rgb(var(--gray-400));
}

.lock-actions {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
}

.lock-updated {
  margin: 0;
  align-self: center;
  font-size: 0.6875rem;
  color: rgb(var(--gray-500));
}

.dark .lock-updated {
  color: rgb(var(--gray-400));
}

@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
  }
  .lock-circle--busy {
    animation: none;
  }
}

/* ── Card face ───────────────────────────────────────────────────────────────
   The shell owns the frame, the name and the size rules; these are the device's
   own parts inside it. */
.lock-card-face__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.lock-card-face__figure {
  width: 66%;
  height: 66%;
}

/* The lock inside the pin: sized off the ring's clear circle, like the tank's. */
.lock-pin-art {
  width: 64%;
  height: 64%;
}

/* The sheet's 6rem circle holds the drawing, not a glyph. */
.lock-circle__art {
  width: 3.6rem;
  height: 3.6rem;
}

.lock-card-face__value {
  flex: none;
  text-align: center;
  font-size: var(--card-value, 0.85rem);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #64748b;
}

.dark .lock-card-face__value {
  color: #94a3b8;
}

/* ── The unlocked card ───────────────────────────────────────────────────────
   A lock is worth a warning only while it stands OPEN. Locked, the card keeps
   the neutral surface every resting device wears; unlocked, it borrows the
   sensor cards' language — a nearly transparent tint and a glowing edge that
   flashes, so a door left open reads from across the room (owner, 25.10.2026:
   *"the breathing should be more like a flashing warning effect. bit faster.
   more visible transitions... for sensor and locks the same"*). The tint is
   deliberately light and the glow carries the state: the shell's own accent
   fill (26 %) would make an unlocked lock look like a lit lamp card. */
.lock-card-face {
  --lock-card-tone: rgb(245, 158, 11);
  --lock-card-tone-low: 0.02;
  --lock-card-tone-high: 0.28;
  --lock-card-halo-low: 0.15;
  --lock-card-breath: 1.7s;
  position: relative;
  isolation: isolate;
}

.dark .lock-card-face {
  --lock-card-tone: rgb(217, 119, 6);
  --lock-card-tone-low: 0.03;
  --lock-card-tone-high: 0.3;
}

/* The shell's own soft edge and faint wash are exactly this card's surface; only
   its static glow is silenced, because the flashing halo below is the signal. */
.lock-card-face.device-card--accent.device-card--on {
  box-shadow: none;
}

.lock-card-face::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1; /* above the card's own fill, below its content */
  border-radius: inherit;
  background: var(--lock-card-tone);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

/* The halo: a crisp 1 px ring and two blurred layers outside the card's edge.
   Only its opacity animates, so the glow is composited like the wash. */
.lock-card-face::after {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: inherit;
  pointer-events: none;
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--lock-card-tone) 60%, transparent),
    0 0 0.75rem color-mix(in srgb, var(--lock-card-tone) 70%, transparent),
    0 0 1.7rem color-mix(in srgb, var(--lock-card-tone) 42%, transparent);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.lock-card-face--unlocked::before {
  animation: lock-card-warn-flash var(--lock-card-breath) ease-in-out infinite;
}

.lock-card-face--unlocked::after {
  animation: lock-card-halo-flash var(--lock-card-breath) ease-in-out infinite;
}

/* The flash: a fast rise, a slower fall (the on-beat lands at 35 % of the
   cycle), so the card reads as pulsing a warning rather than breathing. */
@keyframes lock-card-warn-flash {
  0%,
  100% {
    opacity: var(--lock-card-tone-low);
  }
  35% {
    opacity: var(--lock-card-tone-high);
  }
}

/* The halo flashes in step and never goes fully dark, so the edge stays
   readable while the tint swings. */
@keyframes lock-card-halo-flash {
  0%,
  100% {
    opacity: var(--lock-card-halo-low);
  }
  35% {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  /* `!important` so this beats the file's own `* { animation-duration: 0.01ms
     !important }` reset above — otherwise the breath would snap to its last
     frame instead of holding a steady tint. */
  .lock-card-face--unlocked::before {
    animation: none !important;
    /* A steady tint instead of a flash. */
    opacity: calc((var(--lock-card-tone-low) + var(--lock-card-tone-high)) / 2);
  }

  .lock-card-face--unlocked::after {
    animation: none !important;
    /* A steady ring instead of a flashing one. */
    opacity: 0.85;
  }
}

.lock-card-face__verbs {
  display: flex;
  width: 100%;
  gap: 0.3rem;
}

.lock-card-face__verb {
  display: inline-flex;
  flex: 1 1 0;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  border-radius: 0.5rem;
  background: rgba(148, 163, 184, 0.16);
  padding: 0.25rem 0.3rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: #475569;
}

.lock-card-face__verb--active {
  background: rgba(34, 197, 94, 0.18);
  color: #15803d;
}

.lock-card-face__verb:disabled {
  opacity: 0.45;
}

.lock-card-face__verb .material-symbols-outlined {
  font-size: 1rem;
}

.dark .lock-card-face__verb {
  background: rgba(255, 255, 255, 0.08);
  color: #cbd5e1;
}

.dark .lock-card-face__verb--active {
  background: rgba(34, 197, 94, 0.18);
  color: #86efac;
}
</style>
